import { createAsyncThunk } from '@reduxjs/toolkit';
import type { EndEvent } from 'jssip/lib/RTCSession';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { AppDispatch, RootState } from 'features/app/store/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { forceCancelColdHandoff } from 'features/cold-handoff/store';
import { RemoteMediaEventBus } from 'features/remote-media/services';
import { Originator, RtcSessionTerminateCauses } from 'features/sip/enums';
import { sessionEndAnalytics } from 'features/sip/helpers';
import { RtcSessionTracker } from 'features/sip/services';
import { getConnectionChangeTypeByOriginator } from 'features/sip/utils';
import {
  completeTeaming,
  isTeamingStartedSelector,
  selectIsInPersonTeamingRequest,
  selectTeamingType,
} from 'features/teaming/teaming-base/store';
import { VriChatEventBus } from 'features/vri-chat/services';

// ========================================
// TYPES AND INTERFACES
// ========================================

interface SessionEndContext {
  event: EndEvent;
  isRefer: boolean;
  isTeaming: boolean;
  isDirectSessionEnd: boolean;
  isLeaveTeaming: boolean;
  isLeaveHandoff: boolean;
  isSpecialEndEvent: boolean;
}

// ========================================
// MAIN THUNK
// ========================================

export const handleRtcSessionEndedThunk = createAsyncThunk<
  void,
  { event: EndEvent },
  { state: RootState; dispatch: AppDispatch }
>('sip/handleRtcSessionEnded', async ({ event }, { getState, dispatch }) => {
  const state = getState();
  const isTeaming = isTeamingStartedSelector(state);
  const teamingType = selectTeamingType(state);
  // Build complete context for this session end
  const context = buildSessionEndContext(event, isTeaming);

  // Log session end initiation
  sessionEndAnalytics.sessionEndInitiated(dispatch, {
    event: context.event,
    isRefer: context.isRefer,
    isTeaming: context.isTeaming,
  });

  // Early exit: ignore irrelevant BYE messages during teaming
  const isInPersonTeamingSession = selectIsInPersonTeamingRequest(state);
  if (
    shouldIgnoreByeDuringTeaming(
      event,
      isTeaming,
      dispatch,
      isInPersonTeamingSession
    )
  ) {
    sessionEndAnalytics.ignoringByeMessage(dispatch, teamingType);
    return;
  }

  // Log main session end event
  sessionEndAnalytics.sessionEnded(dispatch, event);

  // Execute main session end workflow
  await handleTeamingCompletion(dispatch, context);
  handleRemoteVideoReset(dispatch, context);
  handleDeafParticipantDisconnect(dispatch, context);
  handleSessionTrackerCleanup(dispatch, context);
  handleGeneralCleanup(dispatch, context);

  // Log completion
  sessionEndAnalytics.completed(dispatch, {
    event: context.event,
    isRefer: context.isRefer,
    isOriginalReferSession: context.isDirectSessionEnd,
    isLeaveTeaming: context.isLeaveTeaming,
    isLeaveHandoff: context.isLeaveHandoff,
    isTeaming: context.isTeaming,
  });
});

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Checks if we should ignore BYE messages during teaming from non-teaming session endpoints
 */

export function shouldIgnoreByeDuringTeaming(
  event: EndEvent,
  isTeaming: boolean,
  dispatch: AppDispatch,
  isInPersonTeamingSession?: boolean
): boolean {
  /**
   * Only ignore BYE if we are teaming AND in a remote teaming (FreeSWITCH) room.
   * Do NOT ignore BYE for in-person ad hoc (AdhocReq) or in-person (Request) teaming.
   * Also we want to process all BYE messages originating from the local user.
   */
  if (!isTeaming || event.originator === Originator.LOCAL) {
    return false;
  }

  // In person teaming doesn't use FreeSWITCH rooms, so always process BYE
  if (isInPersonTeamingSession) {
    dispatch(
      sendAnalyticsInfo({
        Method: 'shouldIgnoreByeDuringTeaming',
        Message:
          'Session End BYE for in-person teaming received, will process it normally',
      })
    );
    return false;
  }

  // Get the BYE message's "from" field
  const fromHeader =
    event.message?.getHeader?.('from') ||
    event.message?.from?.toString?.() ||
    '';

  // Check if the BYE is from the teaming session - if so, DON'T ignore it
  const isFromRemoteTeamingSession = RtcSessionTracker.isTeamingUri(fromHeader);
  if (isFromRemoteTeamingSession) {
    dispatch(
      sendAnalyticsInfo({
        Method: 'shouldIgnoreByeDuringTeaming',
        Message:
          'Session End BYE from teaming session received (remote teaming room)',
      })
    );
    return false; // Don't ignore BYE from teaming session
  }

  // Only ignore BYE if in remote teaming room and not from teaming session
  return true;
}

/**
 * Analyzes the end event to determine special termination conditions
 */
function analyzeEndEvent(event: EndEvent) {
  const isLeaveTeaming =
    event.cause === RtcSessionTerminateCauses.LEAVE_TEAMING;
  const isLeaveHandoff = event.cause === RtcSessionTerminateCauses.HANDOFF;
  const isAdminCanceledCall =
    event.cause === RtcSessionTerminateCauses.ADMIN_CANCELED_CALL;
  const isReturnToQueue =
    event.cause === RtcSessionTerminateCauses.RETURN_TO_QUEUE;
  const isRefer = event.cause === RtcSessionTerminateCauses.REFER;

  const isSpecialEndEvent =
    isLeaveTeaming ||
    isLeaveHandoff ||
    isAdminCanceledCall ||
    isReturnToQueue ||
    isRefer;

  return {
    isLeaveTeaming,
    isLeaveHandoff,
    isAdminCanceledCall,
    isReturnToQueue,
    isRefer,
    isSpecialEndEvent,
  };
}

/**
 * Determines if the ended event is from the direct session
 */
function checkDirectSessionEnd(
  event: EndEvent,
  isRefer: boolean,
  isTeaming: boolean
): boolean {
  const isLocalRefer = isRefer && event.originator === Originator.LOCAL;
  if (isLocalRefer && isTeaming) {
    // Local REFER during teaming - check if direct session exists
    const directSession = RtcSessionTracker.getDirectSession();
    return directSession !== null && directSession !== undefined;
  }
  return false;
}

/**
 * Builds the complete context for session end processing
 */
function buildSessionEndContext(
  event: EndEvent,
  isTeaming: boolean
): SessionEndContext {
  const eventAnalysis = analyzeEndEvent(event);
  const isDirectSessionEnd = checkDirectSessionEnd(
    event,
    eventAnalysis.isRefer,
    isTeaming
  );

  return {
    event,
    isTeaming,
    isDirectSessionEnd,
    ...eventAnalysis,
  };
}

// ========================================
// BUSINESS LOGIC HANDLERS
// ========================================

/**
 * Handles teaming completion logic
 */
async function handleTeamingCompletion(
  dispatch: any,
  context: SessionEndContext
) {
  const { isLeaveTeaming, isRefer } = context;

  if (!isLeaveTeaming && !isRefer) {
    await dispatch(completeTeaming()).unwrap();
  }
}

/**
 * Handles remote video reset logic
 */
function handleRemoteVideoReset(dispatch: any, context: SessionEndContext) {
  const { isRefer, isTeaming } = context;

  if (isRefer && isTeaming) {
    // During a REFER within a teaming session we do not reset remote video
    return;
  }

  RemoteMediaEventBus.resetRemoteVideo$.next();
}

/**
 * Handles deaf participant disconnect events
 */
function handleDeafParticipantDisconnect(
  dispatch: any,
  context: SessionEndContext
) {
  const { event, isRefer, isTeaming, isDirectSessionEnd, isSpecialEndEvent } =
    context;

  const shouldSkipDeafDisconnect = isRefer && isTeaming && isDirectSessionEnd;

  if (shouldSkipDeafDisconnect) {
    sessionEndAnalytics.skipDeafDisconnect(dispatch, {
      isOriginalReferSession: isDirectSessionEnd,
      isRefer,
      isTeaming,
      shouldSkipDeafDisconnect,
    });
  } else {
    // Normal deaf disconnect handling including the UI disconnect.
    sessionEndAnalytics.emitDeafDisconnect(dispatch, event, isSpecialEndEvent);
    ParticipantsStatusEventBus.deaf.$disconnected.next({
      connectionChangeType: getConnectionChangeTypeByOriginator(
        event.originator
      ),
      isConnectionCallStateNeeded: !isSpecialEndEvent,
    });
  }
}

/**
 * Handles session tracker cleanup for REFER sessions
 */
function handleSessionTrackerCleanup(
  dispatch: any,
  context: SessionEndContext
) {
  const { isDirectSessionEnd } = context;

  if (isDirectSessionEnd) {
    RtcSessionTracker.disconnectAndClearDirectSession();
  }
}

/**
 * Handles general end of RTC session tasks such as canceling handoffs and VRI chat functions.
 */
function handleGeneralCleanup(dispatch: any, context: SessionEndContext) {
  const { isLeaveHandoff, isRefer } = context;

  if (!isLeaveHandoff && !isRefer) {
    dispatch(forceCancelColdHandoff());
    VriChatEventBus.$leaveChat.next();
  }
}
